import { ref, type Ref } from 'vue'
import { EventNameEnum } from '@/types/generated'
import { OpenAPI } from '@/types/generated/core/OpenAPI'
import { useMetrics } from '@/composables/useMetrics'
import { VERDICT_SOURCE_PROVIDER } from '@/composables/useVerdictLabels'

interface UseVerdictPdfActionsOptions {
  chatType: Ref<string>
  messageUuid: Ref<string>
}

export function useVerdictPdfActions({ chatType, messageUuid }: UseVerdictPdfActionsOptions) {
  const { trackEvent } = useMetrics()
  const pdfErrorUuids = ref<string[]>([])

  const fetchVerdictPdfUrl = async (uuid: string): Promise<string> => {
    const response = await fetch(`${OpenAPI.BASE}/api/v3/court_judgements/${uuid}/pdf/`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()
    return data.url
  }

  const clearPdfError = (uuid: string) => {
    pdfErrorUuids.value = pdfErrorUuids.value.filter((id) => id !== uuid)
  }

  const baseEventProps = (uuid: string) => ({
    court_judgement_uuid: uuid,
    message_uuid: messageUuid.value,
    chat_type: chatType.value,
    source_provider: VERDICT_SOURCE_PROVIDER
  })

  const openVerdictPdf = async (uuid: string) => {
    clearPdfError(uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      window.open(url, '_blank', 'noopener')
      void trackEvent(EventNameEnum.VERDICT_PDF_OPENED, baseEventProps(uuid))
    } catch {
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_PREVIEW_FAILED, baseEventProps(uuid))
    }
  }

  const downloadVerdictPdf = async (uuid: string, fileNumber: string | null) => {
    clearPdfError(uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      const fileResponse = await fetch(url)
      if (!fileResponse.ok) throw new Error()
      const blob = await fileResponse.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileNumber ? `${fileNumber}.pdf` : `verdict_${uuid}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOADED, baseEventProps(uuid))
    } catch {
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOAD_FAILED, baseEventProps(uuid))
    }
  }

  return {
    pdfErrorUuids,
    openVerdictPdf,
    downloadVerdictPdf
  }
}
